import { Decimal } from 'decimal.js';

/**
 * VAT Classification for different types of supplies
 *
 * UD: Domestic taxable supply in SK (§ 8, § 9) - also used when we charge SK VAT
 *     to EU-B2C customers as long as we stay below the €10 000 OSS threshold.
 *
 * UDZAHR: Place of supply outside Slovakia with right to deduct - covers
 *     (a) EU-B2B digital services under reverse-charge (§ 15 VAT Directive) and
 *     (b) supplies to customers outside the EU (outside scope, right to deduct).
 *
 * UN: DO NOT report in the Slovak VAT return - used for Union-OSS sales
 *     (B2C consumers in other EU member states once OSS is applied).
 */

/**
 * Standard VAT rates for EU countries
 * Key is ISO-3166-1 alpha-2 country code
 * Value is the standard VAT rate as a Decimal
 */
const EU_STANDARD_VAT: Record<string, Decimal> = {
  AT: new Decimal('20'), // Austria
  BE: new Decimal('21'), // Belgium
  BG: new Decimal('20'), // Bulgaria
  HR: new Decimal('25'), // Croatia
  CY: new Decimal('19'), // Cyprus
  CZ: new Decimal('21'), // Czech Republic
  DK: new Decimal('25'), // Denmark
  EE: new Decimal('24'), // Estonia - ↑ from 22 % on 1 Jul 2025
  FI: new Decimal('25.5'), // Finland - ↑ from 24 % on 1 Sep 2024
  FR: new Decimal('20'), // France
  DE: new Decimal('19'), // Germany
  GR: new Decimal('24'), // Greece
  HU: new Decimal('27'), // Hungary
  IE: new Decimal('23'), // Ireland
  IT: new Decimal('22'), // Italy
  LV: new Decimal('21'), // Latvia
  LT: new Decimal('21'), // Lithuania
  LU: new Decimal('17'), // Luxembourg
  MT: new Decimal('18'), // Malta
  NL: new Decimal('21'), // Netherlands
  PL: new Decimal('23'), // Poland
  PT: new Decimal('23'), // Portugal
  RO: new Decimal('19'), // Romania
  SK: new Decimal('23'), // Slovakia - ↑ from 20 % on 1 Jan 2025
  SI: new Decimal('22'), // Slovenia
  ES: new Decimal('21'), // Spain
  SE: new Decimal('25'), // Sweden
};

interface EUCountry {
  code: string;
  name: string;
  vatRate: Decimal;
}

// Helper to get VAT rate with non-null assertion for known EU country codes
const getVatRate = (code: string): Decimal => {
  const rate = EU_STANDARD_VAT[code];
  if (!rate) {
    throw new Error(`Unknown EU country code: ${code}`);
  }
  return rate;
};

export const EU_COUNTRIES: EUCountry[] = [
  { code: 'AT', name: 'Austria', vatRate: getVatRate('AT') },
  { code: 'BE', name: 'Belgium', vatRate: getVatRate('BE') },
  { code: 'BG', name: 'Bulgaria', vatRate: getVatRate('BG') },
  { code: 'HR', name: 'Croatia', vatRate: getVatRate('HR') },
  { code: 'CY', name: 'Cyprus', vatRate: getVatRate('CY') },
  { code: 'CZ', name: 'Czech Republic', vatRate: getVatRate('CZ') },
  { code: 'DK', name: 'Denmark', vatRate: getVatRate('DK') },
  { code: 'EE', name: 'Estonia', vatRate: getVatRate('EE') },
  { code: 'FI', name: 'Finland', vatRate: getVatRate('FI') },
  { code: 'FR', name: 'France', vatRate: getVatRate('FR') },
  { code: 'DE', name: 'Germany', vatRate: getVatRate('DE') },
  { code: 'GR', name: 'Greece', vatRate: getVatRate('GR') },
  { code: 'HU', name: 'Hungary', vatRate: getVatRate('HU') },
  { code: 'IE', name: 'Ireland', vatRate: getVatRate('IE') },
  { code: 'IT', name: 'Italy', vatRate: getVatRate('IT') },
  { code: 'LV', name: 'Latvia', vatRate: getVatRate('LV') },
  { code: 'LT', name: 'Lithuania', vatRate: getVatRate('LT') },
  { code: 'LU', name: 'Luxembourg', vatRate: getVatRate('LU') },
  { code: 'MT', name: 'Malta', vatRate: getVatRate('MT') },
  { code: 'NL', name: 'Netherlands', vatRate: getVatRate('NL') },
  { code: 'PL', name: 'Poland', vatRate: getVatRate('PL') },
  { code: 'PT', name: 'Portugal', vatRate: getVatRate('PT') },
  { code: 'RO', name: 'Romania', vatRate: getVatRate('RO') },
  { code: 'SK', name: 'Slovakia', vatRate: getVatRate('SK') },
  { code: 'SI', name: 'Slovenia', vatRate: getVatRate('SI') },
  { code: 'ES', name: 'Spain', vatRate: getVatRate('ES') },
  { code: 'SE', name: 'Sweden', vatRate: getVatRate('SE') },
].sort((a, b) => a.name.localeCompare(b.name));

/**
 * Non-EU European countries (for address selection only, not for VAT calculation)
 */
interface EuropeanCountry {
  code: string;
  /**
   * English name. NOT a display string — the dropdowns render via
   * Intl.DisplayNames (CLDR), so `TR` shows as "Türkiye", not this "Turkey".
   * Used only as (a) a search keyword in CountryCombobox so typing the English
   * name finds the country, and (b) the localeCompare sort key below.
   */
  name: string;
  nameSk: string;
}

export const OTHER_EUROPEAN_COUNTRIES: EuropeanCountry[] = [
  { code: 'AL', name: 'Albania', nameSk: 'Albánsko' },
  { code: 'AD', name: 'Andorra', nameSk: 'Andorra' },
  { code: 'IS', name: 'Iceland', nameSk: 'Island' },
  { code: 'LI', name: 'Liechtenstein', nameSk: 'Lichtenštajnsko' },
  { code: 'MD', name: 'Moldova', nameSk: 'Moldavsko' },
  { code: 'MC', name: 'Monaco', nameSk: 'Monako' },
  { code: 'ME', name: 'Montenegro', nameSk: 'Čierna Hora' },
  { code: 'MK', name: 'North Macedonia', nameSk: 'Severné Macedónsko' },
  { code: 'NO', name: 'Norway', nameSk: 'Nórsko' },
  { code: 'SM', name: 'San Marino', nameSk: 'San Maríno' },
  { code: 'RS', name: 'Serbia', nameSk: 'Srbsko' },
  { code: 'CH', name: 'Switzerland', nameSk: 'Švajčiarsko' },
  { code: 'TR', name: 'Turkey', nameSk: 'Turecko' },
  { code: 'UA', name: 'Ukraine', nameSk: 'Ukrajina' },
  { code: 'GB', name: 'United Kingdom', nameSk: 'Spojené kráľovstvo' },
].sort((a, b) => a.name.localeCompare(b.name));

/**
 * Non-EU countries list with a generic "Other" option
 * Used for countries outside the European Union
 */
export const NON_EU_COUNTRIES: EUCountry[] = [
  { code: 'XX', name: 'Other', vatRate: new Decimal('0') },
];

const ROUND_HALF_UP = Decimal.ROUND_HALF_UP;
const OSS_THRESHOLD = 10_000.0;

export function invoiceFromGross(
  grossTotal: Decimal,
  rate: Decimal
): [Decimal, Decimal] {
  // Convert % rate to its multiplier (e.g. 20 → 1.20)
  const divisor: Decimal = new Decimal('1').plus(rate.div(new Decimal('100')));

  // Net amount before VAT
  const netAmount: Decimal = grossTotal
    .div(divisor)
    .toDecimalPlaces(2, ROUND_HALF_UP);

  // VAT amount is the residual
  const vatAmount: Decimal = grossTotal
    .minus(netAmount)
    .toDecimalPlaces(2, ROUND_HALF_UP);

  return [vatAmount, netAmount];
}

interface IssuingCompany {
  // ISO-3166 alpha-2
  country: string;
  isVatPayer: boolean;
  ossRegistered: boolean;
  euB2cYearlyTurnover: number;
}

class IssuingCompanyImpl implements IssuingCompany {
  country = 'SK';
  isVatPayer = true;
  ossRegistered = false;
  euB2cYearlyTurnover = 0.0;

  constructor(data?: Partial<IssuingCompany>) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Determine if the company should use Union-OSS based on its status.
   * Returns true if registered for OSS or if it has exceeded the €10,000 threshold.
   */
  useOss(): boolean {
    return this.ossRegistered || this.euB2cYearlyTurnover >= OSS_THRESHOLD;
  }
}

interface Customer {
  // ISO-3166 alpha-2, e.g. 'SK', 'CZ', 'US'
  country: string;
  // True = B2B; False = B2C
  isBusiness: boolean;
  // Customer name, e.g. "John Doe" or "Acme Corp"
  name?: string | undefined;
  // Company name if B2B, e.g. "Acme Corp"
  company?: string | undefined;
  // Business registration number, e.g. IČO in SK
  companyId?: string | undefined;
  // VAT ID of buyer if B2B, or null if not VAT payer, e.g. IČ DPH in SK
  vatId?: string | undefined;
  // Tax ID of buyer if B2B, or null if not required, e.g. DIČ in SK
  taxId?: string | undefined;
  // TODO: you can add address fields
}

class CustomerImpl implements Customer {
  country: string;
  isBusiness = true;
  name?: string | undefined;
  company?: string | undefined;
  companyId?: string | undefined;
  vatId?: string | undefined;
  taxId?: string | undefined;

  constructor(data: Customer) {
    this.country = data.country;
    this.isBusiness = data.isBusiness;
    this.name = data.name;
    this.company = data.company;
    this.companyId = data.companyId;
    this.vatId = data.vatId;
    this.taxId = data.taxId;
  }

  isEu(): boolean {
    return Object.prototype.hasOwnProperty.call(
      EU_STANDARD_VAT,
      this.country.toUpperCase()
    );
  }
}

/**
 * Default issuing company configuration for FinAI
 */
const FINAI: IssuingCompany = new IssuingCompanyImpl({
  country: 'SK',
  isVatPayer: true,
  ossRegistered: false,
  euB2cYearlyTurnover: 0.0,
});

/**
 * Calculate the applicable VAT rate for a customer based on their location and status
 *
 * Rules:
 * • Domestic SK → SK rate
 * • Non-EU → 0%
 * • EU B2B with VAT ID → 0% (reverse charge)
 * • EU B2C:
 *    - if use_oss=True → customer-country rate
 *    - else → SK rate (below €10 000 Art 59c micro-rule)
 *
 * @param customer - The customer to calculate VAT rate for
 * @param issuingCompany - The company issuing the invoice (defaults to FINAI)
 * @returns The applicable VAT rate as a Decimal
 */
function vatRateForCustomer(
  customer: Customer,
  issuingCompany: IssuingCompany = FINAI
): Decimal {
  // Domestic SK → SK rate
  if (customer.country.toUpperCase() === 'SK') {
    return getVatRate('SK');
  }

  // Non-EU → 0%
  if (!new CustomerImpl(customer).isEu()) {
    return new Decimal('0');
  }

  // EU B2B with VAT ID → 0% (reverse charge)
  if (customer.isBusiness && customer.vatId) {
    return new Decimal('0');
  }

  // EU B2C branch
  if (
    issuingCompany instanceof IssuingCompanyImpl
      ? issuingCompany.useOss()
      : issuingCompany.ossRegistered ||
        issuingCompany.euB2cYearlyTurnover >= OSS_THRESHOLD
  ) {
    return EU_STANDARD_VAT[customer.country.toUpperCase()] ?? getVatRate('SK');
  } else {
    return getVatRate('SK');
  }
}

/**
 * Calculate VAT amount and gross total from a net amount and VAT rate
 *
 * @param netAmount - The amount before VAT
 * @param rate - VAT rate as a percentage (e.g. 20 for 20%)
 * @returns [vatAmount, grossTotal] tuple
 */
export function invoiceTotals(
  netAmount: Decimal,
  rate: Decimal
): [Decimal, Decimal] {
  const vatAmount = netAmount
    .mul(rate)
    .div(new Decimal('100'))
    .toDecimalPlaces(2, ROUND_HALF_UP);

  const grossTotal = netAmount
    .plus(vatAmount)
    .toDecimalPlaces(2, ROUND_HALF_UP);

  return [vatAmount, grossTotal];
}

interface VatLabel {
  EN: string;
  SK: string;
}

/**
 * Decide what short VAT label should appear next to the price.
 *
 * @param customer - The customer object
 * @param issuingCompany - The issuing company (defaults to FINAI)
 * @returns Object with English and Slovak wording
 */
function vatLabel(
  customer: Customer,
  issuingCompany: IssuingCompany = FINAI
): VatLabel {
  const rate: Decimal = vatRateForCustomer(customer, issuingCompany);

  // ---------- 1. Positive VAT rate – show "incl. VAT XX %" ----------
  if (rate.gt(0)) {
    // Remove trailing zeros and format as percentage
    const rateStr = `${Number.parseFloat(rate.toFixed(10))} %`;
    return {
      EN: `incl. VAT ${rateStr}`,
      SK: `vrátane DPH ${rateStr}`,
    };
  }

  // ---------- 2. Zero-rate cases – choose the correct reason ----------
  const isEu = customer.country.toUpperCase() in EU_STANDARD_VAT;

  const crossB2b =
    isEu &&
    customer.isBusiness &&
    Boolean(customer.vatId) && // valid VAT-ID will be checked elsewhere
    customer.country !== issuingCompany.country;

  // 2a. EU B2B reverse-charge (Art. 44 & 196)
  if (crossB2b) {
    return {
      EN: 'excl. VAT — reverse charge 0 %',
      SK: 'bez DPH — prenesenie daňovej povinnosti 0 %',
    };
  }

  // 2b. Customer outside the EU – place of supply abroad (export of services)
  if (!isEu) {
    return {
      EN: 'excl. VAT — outside EU scope',
      SK: 'bez DPH — mimo rozsah EÚ',
    };
  }

  // 2c. Supplier is NOT a VAT payer (below registration threshold)
  if (!issuingCompany.isVatPayer) {
    return {
      EN: 'VAT not applicable',
      SK: 'DPH sa neuplatňuje',
    };
  }

  // 2d. Other zero-rate situations (statutory exemptions, etc.)
  return {
    EN: '0 % VAT — exempt',
    SK: '0 % DPH — oslobodené',
  };
}

/**
 * Configuration for displaying prices on the pricing page
 */
interface PricingDisplay {
  /** True → show gross (VAT-inclusive) price */
  includeVat: boolean;
  /** VAT rate to display (e.g. 23 for 23%), or null when no VAT applies */
  vatRate: Decimal | null;
  /** VAT label to display (e.g. "(incl. VAT 23 %)" or "bez DPH – mimo rozsah EÚ") */
  vatLabel: VatLabel;
}

/**
 * Decide whether the pricing page should display amounts WITH or WITHOUT
 * VAT, and—if VAT applies—the percentage to print.
 *
 * @param country - ISO-3166 alpha-2 code from "My country / Moja krajina"
 * @param isBusiness - Checkbox "Som živnostník/firma"
 * @param vatPayer - Checkbox "Platca DPH" (only visible if isBusiness=true)
 * @param issuingCompany - Your own profile (defaults to FINAI)
 * @returns PricingDisplay configuration
 *   • includeVat == true → show gross price and add "(incl. XX % DPH)"
 *   • includeVat == false → show net price only (reverse-charge / outside scope / export of services)
 */
export function pricingPageVatView(
  country = 'SK',
  isBusiness = false,
  vatPayer = false,
  issuingCompany: IssuingCompany = FINAI
): PricingDisplay {
  // 1. Translate UI choices into a Customer object
  const customer: Customer = {
    country,
    isBusiness,
    // Pretend we already have a valid VAT ID once the user ticks
    // "VAT payer". For cross-border EU B2B this triggers reverse-charge.
    vatId: isBusiness && vatPayer ? 'VALID' : undefined,
  };

  // 2. Work out the applicable VAT rate using existing logic
  const rate = vatRateForCustomer(customer, issuingCompany);

  const vatLabelData = vatLabel(customer, issuingCompany);

  // 3. Decide what the pricing page should show
  //
  // B2B VAT payers see net price + "+ DPH" (they reclaim VAT, so net is what matters).
  // Consumers and non-VAT-payers see gross price with "vrátane DPH X %".
  // Reverse-charge and non-EU see net price (rate is 0, handled by existing logic).
  const isDomesticBusinessVatPayer =
    isBusiness && vatPayer && customer.country === issuingCompany.country;

  if (isDomesticBusinessVatPayer && rate.gt(0)) {
    return {
      includeVat: false, // Show net price
      vatRate: rate,
      vatLabel: {
        EN: '+ VAT',
        SK: '+ DPH',
      },
    };
  }

  return {
    includeVat: !rate.equals(0), // Show VAT-inclusive price only when VAT applies
    vatRate: rate.equals(0) ? null : rate, // null for reverse charge and non-EU
    vatLabel: vatLabelData,
  };
}

/** SK standard VAT rate the DB prices are stored inclusive of. */
const SK_GROSS_VAT_RATE = new Decimal('23');

/**
 * Convert a stored **SK-gross** plan price (the DB convention: ``price_cents``
 * and ``price_tiers.unit_amount_eur`` are persisted VAT-inclusive at the SK
 * 23 % rate — see PricingCardV2's monthly-price block, which is the proven
 * reference) into the customer-facing display amount + short VAT suffix for a
 * given country / business / VAT-payer selection.
 *
 * Mirrors PricingCardV2 exactly: strip SK VAT to net, then either re-apply the
 * customer's rate (B2C / non-VAT-payer → gross) or show net (domestic B2B
 * VAT-payer → "+ DPH", reverse-charge / non-EU → 0 %). Extracted so the
 * professional (lawyer) and org (firm) sections share one source of truth
 * instead of re-deriving the math. PricingCardV2 keeps its inlined copy for now
 * (its annual-cycle + launch-promo entanglement is separately fenced); folding
 * it onto this helper is a tracked follow-up.
 *
 * @param grossEur - the SK-gross price in EUR (e.g. ``price_cents / 100``)
 * @returns ``displayPrice`` (already rounded to 2 dp) + localized ``vatSuffix``
 */
export function vatAdjustedDisplayPrice(
  grossEur: number,
  opts: {
    country: string;
    isBusiness: boolean;
    isVatPayer: boolean;
    locale: string;
  }
): { displayPrice: number; vatSuffix: string } {
  const { country, isBusiness, isVatPayer, locale } = opts;
  const vatDisplay = pricingPageVatView(country, isBusiness, isVatPayer);
  const [, netAmount] = invoiceFromGross(
    new Decimal(grossEur),
    SK_GROSS_VAT_RATE
  );
  const vatSuffix =
    locale === 'sk' ? vatDisplay.vatLabel.SK : vatDisplay.vatLabel.EN;

  if (vatDisplay.includeVat && vatDisplay.vatRate) {
    const [, customerGross] = invoiceTotals(netAmount, vatDisplay.vatRate);
    return { displayPrice: customerGross.toNumber(), vatSuffix };
  }
  return { displayPrice: netAmount.toNumber(), vatSuffix };
}

/**
 * Explicit, full-sentence VAT statement for the pricing page — shown once under
 * the VAT controls so the VAT treatment is stated outright, not left to the
 * terse per-card suffix.
 *
 * Verified billing reality (2026-06): the Stripe Prices are
 * ``tax_behavior=unspecified`` and checkout runs with ``automatic_tax``
 * disabled, so Stripe charges exactly the stored amount with no VAT added or
 * itemized. The merchant is an SK VAT payer, so that amount is the **gross**
 * (VAT-inclusive) price — for a consumer the displayed price is the final
 * total. This note makes that explicit.
 *
 * Branches mirror ``pricingPageVatView``:
 *   - consumer / non-VAT-payer (gross shown) → "All prices include X % VAT."
 *   - domestic VAT-payer (net shown)        → "Prices shown excluding VAT — X % VAT is added."
 *   - reverse-charge / non-EU (0 %)         → the view's label verbatim (already the correct explanation).
 */
export function pricingPageVatNote(
  country = 'SK',
  isBusiness = false,
  vatPayer = false,
  issuingCompany: IssuingCompany = FINAI
): VatLabel {
  const view = pricingPageVatView(
    country,
    isBusiness,
    vatPayer,
    issuingCompany
  );
  const rate = view.vatRate
    ? `${Number.parseFloat(view.vatRate.toFixed(10))} %`
    : null;

  // Consumer / non-VAT-payer — gross shown; the displayed price is the total.
  if (view.includeVat && rate) {
    return {
      EN: `All prices include ${rate} VAT.`,
      SK: `Všetky ceny sú vrátane DPH ${rate}.`,
    };
  }

  // Domestic VAT-payer — net shown; VAT added on top (same gross total charged).
  if (!view.includeVat && rate) {
    return {
      EN: `Prices shown excluding VAT — ${rate} VAT is added.`,
      SK: `Ceny sú uvedené bez DPH — pripočíta sa DPH ${rate}.`,
    };
  }

  // 0 % cases (reverse charge / outside EU) — the view's label is already the
  // clear, legally-correct explanation.
  return view.vatLabel;
}
